/**
 * @fileoverview Shared faceted multi-select filter popover
 *
 * Purpose: Consistent filter dropdown across Jobs, Job Contacts, and Firm
 * Contacts. Renders a combobox-style trigger that shows the selected value
 * (or count) and a Command palette with checkable options + facet counts.
 *
 * Standalone — does not depend on @tanstack/react-table. Consumers that use
 * TanStack Table can bridge via `column.getFilterValue()` / `setFilterValue()`.
 *
 * @example
 * <FacetedFilter
 *   title="Status"
 *   options={[{ value: "active", label: "Active" }]}
 *   selectedValues={selected}
 *   onSelectionChange={setSelected}
 *   facetCounts={countsMap}
 * />
 */

import { useState, type ReactNode } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/ui/primitives/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/ui/composed/menus/command";
import { cn } from "@/utils/misc";

export interface FacetedFilterOption {
  value: string;
  label: string;
}

export interface FacetedFilterProps {
  title: string;
  options: ReadonlyArray<FacetedFilterOption>;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  /** Per-option occurrence counts displayed beside each label. */
  facetCounts?: Map<string, number>;
  /** Custom renderer for option labels (trigger + list). */
  renderLabel?: (option: FacetedFilterOption) => ReactNode;
}

export function FacetedFilter({
  title,
  options,
  selectedValues,
  onSelectionChange,
  facetCounts,
  renderLabel,
}: FacetedFilterProps) {
  const [open, setOpen] = useState(false);
  const selected = new Set(selectedValues);

  const renderOption = (option: FacetedFilterOption) =>
    renderLabel ? renderLabel(option) : <span>{option.label}</span>;

  const onlySelected =
    selected.size === 1 ? options.find((o) => selected.has(o.value)) : null;

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onSelectionChange(Array.from(next));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="h-9 min-w-[140px] justify-between gap-1.5 text-xs font-normal"
        >
          {selected.size === 0 ? (
            <span className="truncate">{title}</span>
          ) : onlySelected ? (
            <span className="flex min-w-0 items-center gap-1.5 truncate">
              <span className="text-primary/60">{title}:</span>
              {renderOption(onlySelected)}
            </span>
          ) : (
            <span className="truncate">
              {title} ({selected.size})
            </span>
          )}
          <ChevronsUpDown className="size-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${title.toLowerCase()}...`} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.has(option.value);
                const count = facetCounts?.get(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    value={option.label}
                    onSelect={() => toggle(option.value)}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                    <span className="min-w-0 flex-1">
                      {renderOption(option)}
                    </span>
                    {count !== undefined && (
                      <span className="ml-2 shrink-0 font-mono text-xs text-muted-foreground">
                        {count}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selected.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onSelectionChange([])}
                    className="justify-center text-center"
                  >
                    Clear
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
